import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, FilterGrid } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import { areasService, ServiceArea } from '../../../services/areasService';
import reportService, { FinanceMonthEndResponse, FinanceMonthEndRow } from '../../../services/reportService';
import Toast from '../../../components/Toast';
import { monthHeaderText, PLACEHOLDER_MONTH_HEADERS } from '../utils/monthHeader';

interface FinanceMonthEndReportViewProps {
  serviceAreas: ServiceArea[];
  canViewFinanceMonthEnd: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const formatDollar = (value: number): string =>
  value ? '$' + Math.round(value).toLocaleString('en-US') : '-';

const formatQty = (value: number): string =>
  value ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '-';

// Sticky left columns: Activity # | Activity Name | FMA | Job
const STICKY = [
  { key: 'activityNumber', label: 'Activity #', width: 110 },
  { key: 'activityName', label: 'Activity Name', width: 240 },
  { key: 'foremanArea', label: 'FMA', width: 130 },
  { key: 'job', label: 'Job', width: 60 }
] as const;
// The value-to-date column is pinned right and the grand total pinned bottom so the totals
// stay in view while scrolling; the shadows mark the frozen edges.
const TOTAL_COL_WIDTH = 140;
const FROZEN_RIGHT_EDGE = 'tw-shadow-[-2px_0_4px_rgba(0,0,0,0.12)]';
const FROZEN_BOTTOM_EDGE = 'tw-shadow-[0_-2px_4px_rgba(0,0,0,0.12)]';
const stickyLeft = (i: number) => STICKY.slice(0, i).reduce((sum, c) => sum + c.width, 0);

interface ActivityGroup {
  summary: FinanceMonthEndRow;
  foremanAreas: FinanceMonthEndRow[];
}

/** Split the flat server rows (summary, its foreman areas, ..., grand total) into activity groups. */
function groupRows(rows: FinanceMonthEndRow[]): { groups: ActivityGroup[]; grandTotal: FinanceMonthEndRow | null } {
  const groups: ActivityGroup[] = [];
  let grandTotal: FinanceMonthEndRow | null = null;
  for (const row of rows) {
    if (row.rowType === 'summary') groups.push({ summary: row, foremanAreas: [] });
    else if (row.rowType === 'foremanArea') groups[groups.length - 1]?.foremanAreas.push(row);
    else grandTotal = row;
  }
  return { groups, grandTotal };
}

const rowBg = (row: FinanceMonthEndRow) =>
  row.rowType === 'grandTotal' ? 'tw-bg-surface-alt tw-font-bold'
    : row.rowType === 'summary' ? 'tw-bg-surface-alt tw-font-semibold'
      : 'tw-bg-surface';

/**
 * Finance Month-End report: actual quantity + value by quantified activity and foreman area,
 * per contract month. Closed/finalized tasks only. Replaces the manual QP tracker spreadsheet.
 */
const FinanceMonthEndReportView: React.FC<FinanceMonthEndReportViewProps> = ({
  serviceAreas,
  canViewFinanceMonthEnd,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [serviceArea, setServiceArea] = useState(defaultServiceAreaId || '');
  const [quantifiedYear, setQuantifiedYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [yearOptions, setYearOptions] = useState<number[]>([]);
  const [loadingYears, setLoadingYears] = useState(false);
  const [report, setReport] = useState<FinanceMonthEndResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  // Activities start collapsed to their Summary Total row; expanding one shows its foreman areas.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { groups, grandTotal } = useMemo(() => groupRows(report?.rows ?? []), [report]);

  const toggleActivity = (activityNumber: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(activityNumber)) next.delete(activityNumber);
      else next.add(activityNumber);
      return next;
    });

  useEffect(() => {
    if (defaultServiceAreaId && !serviceArea) setServiceArea(defaultServiceAreaId);
  }, [defaultServiceAreaId]);

  // Quantified-year options come from the service area's Open/Closed quantified plans,
  // same as Quantified Value / Quantified Costing.
  useEffect(() => {
    setReport(null);
    const serviceAreaId = parseInt(serviceArea, 10);
    if (!serviceArea || Number.isNaN(serviceAreaId)) {
      setYearOptions([]);
      setQuantifiedYear('');
      return;
    }
    let cancelled = false;
    setLoadingYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligible = plans.filter((p) => p.status === 'Open' || p.status === 'Closed');
        const years = Array.from(new Set(eligible.map((p) => p.year))).sort((a, b) => b - a);
        // Default to the current contract year: the service area's active plan, else the plan
        // whose date range covers today, else the latest open plan.
        const now = new Date();
        const activePlan = eligible.find((p) => p.isActive);
        const coveringPlan = eligible.find((p) => new Date(p.startDate) <= now && now <= new Date(p.endDate));
        const openYears = eligible.filter((p) => p.status === 'Open').map((p) => p.year);
        const defaultYear = activePlan?.year
          ?? coveringPlan?.year
          ?? (openYears.length > 0 ? Math.max(...openYears) : years[0]);
        setYearOptions(years);
        setQuantifiedYear(defaultYear != null ? String(defaultYear) : '');
      })
      .catch(() => {
        if (!cancelled) {
          setYearOptions([]);
          setQuantifiedYear('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serviceArea]);

  const requireFilters = () => {
    if (!serviceArea || !quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return false;
    }
    return true;
  };

  const handleRun = async () => {
    if (!requireFilters()) return;
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getFinanceMonthEnd({
        serviceAreaId: parseInt(serviceArea, 10),
        quantifiedYear: parseInt(quantifiedYear, 10),
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
      setReport(data);
      setExpanded(new Set());
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Failed to generate the Month-End report. Please try again later.');
      setReport(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExport = async () => {
    if (!requireFilters()) return;
    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadFinanceMonthEnd({
        serviceAreaId: parseInt(serviceArea, 10),
        quantifiedYear: parseInt(quantifiedYear, 10),
        startDate: startDate || undefined,
        endDate: endDate || undefined
      });
      setShowToast(true);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Failed to export the Month-End report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  if (!canViewFinanceMonthEnd) {
    return <Alert variant="warning">You do not have permission to view the Month-End report.</Alert>;
  }

  const monthHeaders = report?.monthHeaders?.length
    ? report.monthHeaders
    : PLACEHOLDER_MONTH_HEADERS;
  const skipped = report
    ? report.diagnostics.tasksSkippedNoDate + report.diagnostics.tasksSkippedOutsideMonths + report.diagnostics.tasksWithoutForemanArea
    : 0;

  const renderRow = (
    row: FinanceMonthEndRow,
    opts: { onClick?: () => void; isOpen?: boolean; frozenBottom?: boolean } = {}
  ) => {
    const bg = rowBg(row);
    const isGrand = row.rowType === 'grandTotal';
    const edge = opts.frozenBottom ? FROZEN_BOTTOM_EDGE : '';
    return (
      <tr
        key={`${row.rowType}-${row.activityNumber}-${row.foremanAreaId ?? 'x'}`}
        onClick={opts.onClick}
        className={opts.onClick ? 'tw-cursor-pointer hover:tw-brightness-95' : undefined}
        aria-expanded={opts.onClick ? opts.isOpen : undefined}
      >
        {STICKY.map((c, i) => (
          <td
            key={c.key}
            className={`${bg} ${edge} tw-border tw-border-line tw-px-2 tw-py-1 tw-text-sm tw-sticky ${opts.frozenBottom ? 'tw-z-30' : 'tw-z-10'} tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap ${c.key === 'foremanArea' && row.rowType === 'foremanArea' ? 'tw-pl-5' : ''}`}
            style={{ left: stickyLeft(i), minWidth: c.width, maxWidth: c.width }}
            title={String(row[c.key] ?? '')}
          >
            {i === 0 && opts.onClick && (
              <i className={`bi ${opts.isOpen ? 'bi-chevron-down' : 'bi-chevron-right'} tw-mr-1 tw-text-content-muted`} aria-hidden="true"></i>
            )}
            {row[c.key]}
          </td>
        ))}
        {row.months.map((cell, mi) => (
          <React.Fragment key={mi}>
            <td className={`${bg} ${edge} ${bodyCell}`}>{isGrand ? '' : formatQty(cell.qty)}</td>
            <td className={`${bg} ${edge} ${bodyCell}`}>{formatDollar(cell.value)}</td>
          </React.Fragment>
        ))}
        <td className={`${bg} ${edge} ${bodyCell}`}>{isGrand ? '' : formatQty(row.totalQty)}</td>
        <td className={`${bg} ${edge} ${bodyCell} tw-text-center`}>{row.unitOfMeasure}</td>
        <td
          className={`${bg} ${edge} ${bodyCell} tw-sticky ${opts.frozenBottom ? 'tw-z-30' : 'tw-z-10'} ${FROZEN_RIGHT_EDGE}`}
          style={{ right: 0, minWidth: TOTAL_COL_WIDTH }}
        >
          {formatDollar(row.totalValue)}
        </td>
      </tr>
    );
  };

  const headCell = 'tw-bg-surface-alt tw-border tw-border-line tw-px-2 tw-py-1 tw-text-center tw-text-xs tw-font-semibold tw-whitespace-nowrap';
  const bodyCell = 'tw-border tw-border-line tw-px-2 tw-py-1 tw-text-right tw-text-sm tw-whitespace-nowrap';

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0 tw-overflow-auto">
      <div className="tw-shrink-0">
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        {skipped > 0 && report && (
          <Alert variant="warning" className="tw-mb-3 tw-py-2">
            {skipped} closed task{skipped === 1 ? '' : 's'} excluded
            {report.diagnostics.tasksSkippedNoDate > 0 && ` · ${report.diagnostics.tasksSkippedNoDate} with no completion date`}
            {report.diagnostics.tasksSkippedOutsideMonths > 0 && ` · ${report.diagnostics.tasksSkippedOutsideMonths} completed outside the 12 contract months`}
            {report.diagnostics.tasksWithoutForemanArea > 0 && ` · ${report.diagnostics.tasksWithoutForemanArea} with no foreman area`}
          </Alert>
        )}

        <ReportParametersCard
          onGenerate={handleRun}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                disabled={loadingServiceAreas}
              >
                <option value="">Select service area</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={quantifiedYear}
                onChange={(e) => {
                  setQuantifiedYear(e.target.value);
                  setReport(null);
                }}
                disabled={!serviceArea || loadingYears || yearOptions.length === 0}
              >
                <option value="">
                  {!serviceArea
                    ? 'Select service area first'
                    : loadingYears
                      ? 'Loading years…'
                      : yearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>

      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-min-w-0">
        <Card className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-min-w-0">
          <Card.Header className="tw-flex tw-justify-between tw-items-center tw-shrink-0">
            <h6 className="tw-mb-0">
              Month-End Report{' '}
              <span className="tw-text-content-muted tw-font-normal tw-text-sm">
                Actual quantity and value by activity and foreman area (closed and finalized tasks)
              </span>
            </h6>
            {report && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setExpanded(new Set(groups.map((g) => g.summary.activityNumber)))}
                >
                  <i className="bi bi-arrows-expand tw-mr-1"></i>
                  Expand all
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setExpanded(new Set())}>
                  <i className="bi bi-arrows-collapse tw-mr-1"></i>
                  Collapse all
                </Button>
                <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={loadingExport}>
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              </div>
            )}
          </Card.Header>
          <Card.Body className="tw-flex-1 tw-min-h-0 tw-min-w-0 tw-flex tw-flex-col" style={{ padding: 0 }}>
            <div className="tw-overflow-auto tw-min-h-0 tw-min-w-0 tw-h-full">
              {!report ? (
                <div className="tw-text-center tw-py-5 tw-text-content-muted">
                  {!serviceArea || !quantifiedYear
                    ? 'Select a Service Area and Quantified Year, then run the report.'
                    : 'Run the report to load month-end values.'}
                </div>
              ) : (
                <table className="tw-border-collapse" style={{ width: 'max-content', minWidth: '100%' }}>
                  <thead className="tw-sticky tw-top-0 tw-z-20">
                    <tr>
                      {STICKY.map((c, i) => (
                        <th
                          key={c.key}
                          rowSpan={2}
                          className={`${headCell} tw-sticky tw-z-30 tw-text-left`}
                          style={{ left: stickyLeft(i), minWidth: c.width, maxWidth: c.width }}
                        >
                          {c.label}
                        </th>
                      ))}
                      {monthHeaders.map((m) => (
                        <th key={m.label} colSpan={2} className={headCell}>
                          {monthHeaderText(m, monthHeaders.indexOf(m))}
                        </th>
                      ))}
                      <th rowSpan={2} className={headCell}>Total Accomplished</th>
                      <th rowSpan={2} className={headCell}>Units</th>
                      <th
                        rowSpan={2}
                        className={`${headCell} tw-sticky tw-z-30 ${FROZEN_RIGHT_EDGE}`}
                        style={{ right: 0, minWidth: TOTAL_COL_WIDTH }}
                      >
                        Actual Value to Date ($)
                      </th>
                    </tr>
                    <tr>
                      {monthHeaders.map((m) => (
                        <React.Fragment key={m.label}>
                          <th className={headCell} style={{ minWidth: 70 }}>Qty</th>
                          <th className={headCell} style={{ minWidth: 90 }}>$</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => {
                      const activityNumber = group.summary.activityNumber;
                      const isOpen = expanded.has(activityNumber);
                      return (
                        <React.Fragment key={activityNumber}>
                          {/* Only activities with foreman-area data can expand; blank rows are
                              suppressed server-side, so an activity with no work has none. */}
                          {renderRow(
                            group.summary,
                            group.foremanAreas.length > 0
                              ? { onClick: () => toggleActivity(activityNumber), isOpen }
                              : {}
                          )}
                          {isOpen && group.foremanAreas.map((row) => renderRow(row))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {grandTotal && (
                    <tfoot className="tw-sticky tw-bottom-0 tw-z-20">
                      {renderRow(grandTotal, { frozenBottom: true })}
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <Toast
        message="Month-End report exported successfully!"
        type="success"
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default FinanceMonthEndReportView;
